import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useHistory } from 'react-router-dom';
import API_BASE_URL from '../config';
import './CreatePost.css';

const CreatePost: React.FC = () => {
    const { accessToken, logout, loading: authLoading } = useAuth();
    const history = useHistory();
    const [platform, setPlatform] = useState<'instagram' | 'whatsapp'>('whatsapp');
    const [caption, setCaption] = useState('');
    const [image, setImage] = useState<File | null>(null);
    const [imageUrl, setImageUrl] = useState('');
    const [familySide, setFamilySide] = useState<'groom' | 'bride' | ''>('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [posts, setPosts] = useState<any[]>([]);
    const [editingPostId, setEditingPostId] = useState<string | null>(null);
    const [sharedInApp, setSharedInApp] = useState(false);
    const [sharedWith, setSharedWith] = useState<'bride' | 'groom' | 'both'>('both');

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch Profile
                const profileRes = await fetch(`${API_BASE_URL}/api/admin/profile`, {
                    headers: { Authorization: `Bearer ${accessToken}` },
                });
                const profileData = await profileRes.json();
                if (profileRes.ok) {
                    setFamilySide(profileData.admin.familySide);
                }

                // Fetch Posts History
                const postsRes = await fetch(`${API_BASE_URL}/api/posts`, {
                    headers: { Authorization: `Bearer ${accessToken}` },
                });
                const postsData = await postsRes.json();
                if (postsRes.ok) {
                    setPosts(postsData.posts);
                }
            } catch (err) {
                console.error('Error fetching data:', err);
            } finally {
                setLoading(false);
            }
        };

        if (!authLoading) {
            if (accessToken) {
                fetchData();
            } else {
                history.push('/login');
            }
        }
    }, [accessToken, authLoading, history]);

    const handleGenerateAI = async (type: string) => {
        setIsGenerating(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/posts/generate-ai`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({ platform, type }),
            });
            const data = await response.json();
            if (response.ok) {
                setCaption(data.caption);
            }
        } catch (err) {
            alert('AI Generation failed. Please try again.');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setImage(file);
            setImageUrl(URL.createObjectURL(file));
        }
    };

    const handleShare = async () => {
        if (!caption) return;

        const shareData = {
            title: 'Wedding Update',
            text: caption,
            url: window.location.origin
        };

        // Try native share API first (mostly mobile)
        if (navigator.share) {
            try {
                await navigator.share(shareData);
            } catch (err) {
                console.log('Share failed or cancelled');
            }
        } else {
            // Fallbacks for Desktop
            if (platform === 'whatsapp') {
                const waUrl = `https://wa.me/?text=${encodeURIComponent(caption)}`;
                window.open(waUrl, '_blank');
            } else {
                const mailUrl = `mailto:?subject=Wedding Update&body=${encodeURIComponent(caption)}`;
                window.open(mailUrl);
            }
        }
    };

    const handlePublish = async (isSharedInApp: boolean = sharedInApp) => {
        if (!caption) {
            alert('Please add a caption first!');
            return;
        }

        setIsPublishing(true);
        try {
            let finalImageUrl = '';

            // Upload image if exists
            if (image) {
                const formData = new FormData();
                formData.append('image', image);
                const uploadRes = await fetch(`${API_BASE_URL}/api/posts/upload`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${accessToken}` },
                    body: formData,
                });
                const uploadData = await uploadRes.json();
                finalImageUrl = uploadData.url;
            }

            // Create or Update post
            const url = editingPostId
                ? `${API_BASE_URL}/api/posts/${editingPostId}`
                : `${API_BASE_URL}/api/posts`;

            const method = editingPostId ? 'PUT' : 'POST';

            const postRes = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    platform,
                    caption,
                    imageUrl: finalImageUrl || imageUrl,
                    status: 'published',
                    aiGenerated: !editingPostId,
                    sharedInApp: isSharedInApp,
                    sharedWith: sharedWith,
                }),
            });

            if (postRes.ok) {
                alert(editingPostId ? 'Post updated!' : 'Post saved!');
                setEditingPostId(null);
                setCaption('');
                setImage(null);
                setImageUrl('');
                setSharedInApp(false);
                setSharedWith('both');

                // Refresh list
                const refreshedPosts = await fetch(`${API_BASE_URL}/api/posts`, {
                    headers: { Authorization: `Bearer ${accessToken}` },
                });
                const refreshData = await refreshedPosts.json();
                setPosts(refreshData.posts);
            }
        } catch (err) {
            alert('Failed to save post.');
        } finally {
            setIsPublishing(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Delete this post?')) return;
        try {
            await fetch(`${API_BASE_URL}/api/posts/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            setPosts(posts.filter(p => p._id !== id));
        } catch (err) {
            alert('Delete failed');
        }
    };

    const handleEdit = (post: any) => {
        setEditingPostId(post._id);
        setPlatform(post.platform);
        setCaption(post.caption);
        setImageUrl(post.imageUrl || '');
        setSharedInApp(post.sharedInApp || false);
        setSharedWith(post.sharedWith || 'both');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (authLoading || loading) return <div className="create-post-container">Loading...</div>;

    return (
        <div className={`admin-dashboard create-post-container ${familySide ? `theme-${familySide}` : ''}`}>

            <div className="create-post-header">
                <div className="create-post-title">
                    <h1>Create Social Post</h1>
                    <p>Generate magic for Instagram and WhatsApp with AI</p>
                </div>
            </div>

            <div className="create-post-content">
                <div className="post-form-card">
                    <div className="platform-selector">
                        <button
                            className={`platform-btn ${platform === 'whatsapp' ? 'active' : ''}`}
                            onClick={() => setPlatform('whatsapp')}
                        >
                            <span>📱</span> WhatsApp Status
                        </button>
                        <button
                            className={`platform-btn ${platform === 'instagram' ? 'active' : ''}`}
                            onClick={() => setPlatform('instagram')}
                        >
                            <span>📸</span> Instagram Post
                        </button>
                    </div>

                    <div className="ai-generate-bar">
                        <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}>
                            ✨ AI Assistant:
                        </span>
                        <button className="ai-btn" onClick={() => handleGenerateAI('countdown')} disabled={isGenerating}>
                            {isGenerating ? 'Wait...' : 'Countdown Hook'}
                        </button>
                        <button className="ai-btn" onClick={() => handleGenerateAI('invitation')} disabled={isGenerating}>
                            {isGenerating ? 'Wait...' : 'Invitation Style'}
                        </button>
                    </div>

                    <div className="form-group">
                        <label className="team-label" style={{ textAlign: 'left', marginBottom: '10px' }}>Upload Media</label>
                        <label className="image-upload-zone">
                            <input type="file" hidden onChange={handleImageChange} accept="image/*" />
                            {imageUrl ? (
                                <img src={imageUrl} alt="Preview" className="upload-preview" />
                            ) : (
                                <>
                                    <span style={{ fontSize: '2rem' }}>📁</span>
                                    <span>Click to upload image</span>
                                </>
                            )}
                        </label>
                    </div>

                    <div className="form-group">
                        <label className="team-label" style={{ textAlign: 'left', marginBottom: '10px' }}>Caption / Content</label>
                        <textarea
                            className="caption-area"
                            placeholder="Type your caption here or use the AI buttons above..."
                            value={caption}
                            onChange={(e) => setCaption(e.target.value)}
                        ></textarea>
                    </div>

                    <div className="form-group">
                        <label className="team-label" style={{ textAlign: 'left', marginBottom: '10px' }}>Share with Guests of</label>
                        <div className="platform-selector" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' }}>
                            <button
                                className={`platform-btn ${sharedWith === 'bride' ? 'active' : ''}`}
                                onClick={() => setSharedWith('bride')}
                                type="button"
                            >
                                🌸 Bride
                            </button>
                            <button
                                className={`platform-btn ${sharedWith === 'groom' ? 'active' : ''}`}
                                onClick={() => setSharedWith('groom')}
                                type="button"
                            >
                                👔 Groom
                            </button>
                            <button
                                className={`platform-btn ${sharedWith === 'both' ? 'active' : ''}`}
                                onClick={() => setSharedWith('both')}
                                type="button"
                            >
                                💍 Both
                            </button>
                        </div>
                        <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '5px' }}>
                            This determines which guests will see this post in their feed if "Share to Guest Feed" is selected.
                        </p>
                    </div>

                    <div className="form-actions" style={{ display: 'flex', gap: '1rem' }}>
                        <button className="publish-btn" onClick={() => handlePublish(false)} disabled={isPublishing}>
                            {isPublishing ? 'Publishing...' : 'Save Social Draft'}
                        </button>
                        <button
                            className="publish-btn"
                            style={{ background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)' }}
                            onClick={() => handlePublish(true)}
                            disabled={isPublishing || !caption}
                        >
                            <span>✨</span> Share to Guest Feed
                        </button>
                        <button
                            className="publish-btn share-btn"
                            style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}
                            onClick={handleShare}
                            disabled={!caption}
                        >
                            <span>📱</span> Social Share
                        </button>
                    </div>
                </div>

                <div className="post-preview-card">
                    <h3 className="team-label" style={{ textAlign: 'left', marginBottom: '1.5rem' }}>Live Preview</h3>

                    {platform === 'instagram' ? (
                        <div className="insta-preview">
                            <div className="insta-header">
                                <div className="insta-avatar"></div>
                                <span style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Team {familySide}</span>
                            </div>
                            <img src={imageUrl || 'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&q=80&w=2070'} alt="Post preview" className="insta-image" />
                            <div className="insta-footer">
                                <div style={{ display: 'flex', gap: '15px', marginBottom: '10px' }}>
                                    <span>❤️</span> <span>💬</span> <span>✈️</span>
                                </div>
                                <p className="insta-caption">
                                    <span style={{ fontWeight: 'bold', marginRight: '5px' }}>Team_{familySide}</span>
                                    {caption || 'Your AI generated caption will appear here...'}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="whatsapp-preview" style={{ background: '#075E54', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                            <div style={{ background: '#128C7E', padding: '10px', borderRadius: '8px', marginBottom: '15px' }}>
                                <span style={{ color: 'white', fontWeight: 'bold' }}>WhatsApp Status</span>
                            </div>
                            <div style={{ position: 'relative', width: '100%', aspectRatio: '9/16', background: '#000', borderRadius: '8px', overflow: 'hidden' }}>
                                <img src={imageUrl || 'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&q=80&w=2070'} alt="Status preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                <div style={{ position: 'absolute', bottom: '0', left: '0', right: '0', background: 'rgba(0,0,0,0.5)', padding: '15px', backdropFilter: 'blur(5px)' }}>
                                    <p style={{ color: 'white', fontSize: '0.9rem' }}>{caption || 'Status text...'}</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="posts-history-section">
                <div className="history-title-container">
                    <h2 className="history-title">Recent Generations</h2>
                    <p className="history-subtitle">Your AI-Powered Social Canvas</p>
                </div>
                <div className="posts-grid">
                    {posts.map((post) => (
                        <div key={post._id} className="post-history-card">
                            <div className="platform-icon-floating">
                                {post.platform === 'whatsapp' ? '📱' : '📸'}
                            </div>

                            <div className="history-actions-overlay">
                                <button className="history-btn-mini" onClick={() => handleEdit(post)} title="Edit">✏️</button>
                                <button className="history-btn-mini" onClick={() => handleDelete(post._id)} title="Delete">🗑️</button>
                            </div>

                            <div className="history-image-container">
                                <img src={post.imageUrl || 'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&q=80&w=2070'} className="history-image" alt="Post" />
                                <div className="history-content-overlay">
                                    <p className="history-caption-premium">
                                        {post.caption}
                                    </p>
                                </div>
                            </div>

                            <div className="history-footer">
                                <div className="history-meta-tags">
                                    <span className="meta-tag-pill">{post.platform}</span>
                                    {post.sharedInApp && (
                                        <span className="meta-tag-pill tag-internal-premium">✨ Internal</span>
                                    )}
                                    {post.sharedInApp && post.sharedWith && (
                                        <span className="meta-tag-pill">👥 {post.sharedWith}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default CreatePost;

